import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, CalendarCheck, Wallet, ListTodo, AlertTriangle } from "lucide-react";
import { formatDate, formatIDR, daysUntil } from "../lib/format";
import { divisionById, statusBadge } from "../lib/constants";

const PAY_TONE = { Lunas: "bg-emerald-50 text-emerald-700 border-emerald-200", DP: "bg-amber-50 text-amber-800 border-amber-200", "Belum Bayar": "bg-rose-50 text-rose-700 border-rose-200" };

export default function BookingsTasks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fDiv, setFDiv] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/bookings/task-summary").then(({ data }) => { setRows(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (fDiv !== "all" && !(r.division_ids || []).includes(fDiv)) return false;
    if (fStatus !== "all" && r.status !== fStatus) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!`${r.client_name} ${r.booking_code} ${r.event_name || ""}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [rows, q, fDiv, fStatus]);

  const kpi = useMemo(() => {
    const total = rows.length;
    const tasksAvail = rows.reduce((a, r) => a + (r.task_counts?.available || 0), 0);
    const tasksClaimed = rows.reduce((a, r) => a + (r.task_counts?.claimed || 0) + (r.task_counts?.in_progress || 0), 0);
    const overdue = rows.filter((r) => r.event_date && daysUntil(r.event_date) < 0 && r.status !== "Completed").length;
    return { total, tasksAvail, tasksClaimed, overdue };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="bookings-tasks-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#1E2022]">Tugas Berdasarkan Booking</h1>
        <p className="text-sm text-[#5C6066] mt-1">Lihat semua booking, pantau tugas, dan kelola progres per divisi dalam satu tempat.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: CalendarCheck, label: "Booking Aktif", val: kpi.total, tone: "text-[#B7A175]", testId: "bt-kpi-total" },
          { icon: ListTodo, label: "Tugas Tersedia", val: kpi.tasksAvail, tone: "text-amber-700", testId: "bt-kpi-avail" },
          { icon: Wallet, label: "Tugas Diambil / Aktif", val: kpi.tasksClaimed, tone: "text-sky-700", testId: "bt-kpi-claimed" },
          { icon: AlertTriangle, label: "Acara Terlambat", val: kpi.overdue, tone: "text-rose-700", testId: "bt-kpi-overdue" },
        ].map((k) => (
          <Card key={k.label} className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid={k.testId}>
            <div className="flex items-center gap-2">
              <k.icon className={`w-5 h-5 ${k.tone}`} />
              <div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">{k.label}</div>
            </div>
            <div className="mt-1 font-display text-2xl font-bold">{k.val}</div>
          </Card>
        ))}
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none">
        <div className="p-4 border-b border-[#E5E0D8] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#8C9096]" />
            <Input className="pl-9 h-9" placeholder="Cari booking, nama client, atau tugas..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="bt-search" />
          </div>
          <Select value={fDiv} onValueChange={setFDiv}>
            <SelectTrigger className="w-44 h-9" data-testid="bt-filter-div"><SelectValue placeholder="Divisi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Divisi</SelectItem>
              {["div-mua","div-decor","div-photo","div-attire","div-music","div-wo","div-warawiri","div-effect","div-adat","div-snap","div-handcraft","div-studio"].map((d) => (
                <SelectItem key={d} value={d}>{divisionById(d)?.short || d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="bt-filter-status"><SelectValue placeholder="Status Booking" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {["Tentative","Confirmed","Completed","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-[#5C6066]">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold">Booking ID</th>
                <th className="px-4 py-2 font-semibold">Nama Client</th>
                <th className="px-4 py-2 font-semibold">Tanggal Acara</th>
                <th className="px-4 py-2 font-semibold">Paket</th>
                <th className="px-4 py-2 font-semibold">Divisi Terkait</th>
                <th className="px-4 py-2 font-semibold">Tugas</th>
                <th className="px-4 py-2 font-semibold">Progress</th>
                <th className="px-4 py-2 font-semibold">Pembayaran</th>
                <th className="px-4 py-2 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EBE0]">
              {loading && <tr><td colSpan={9} className="text-center py-10 text-[#8C9096]">Memuat…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8C9096]">Tidak ada booking.</td></tr>}
              {filtered.map((r) => {
                const tc = r.task_counts || {};
                const total = tc.total || 0;
                const avail = tc.available || 0;
                return (
                  <tr key={r.booking_id} className="hover:bg-[#FAF6EE]/40" data-testid={`bt-row-${r.booking_code}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.booking_code}</td>
                    <td className="px-4 py-3 font-medium">{r.client_name}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{r.event_date ? formatDate(r.event_date) : "-"}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6066] max-w-[180px] truncate" title={r.package_name}>{r.package_name || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.division_ids || []).slice(0, 3).map((d) => (
                          <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${divisionById(d)?.color || "bg-zinc-100 text-zinc-700 border-zinc-200"} border`}>{divisionById(d)?.short || d}</span>
                        ))}
                        {(r.division_ids || []).length > 3 && <span className="text-[10px] text-[#8C9096]">+{r.division_ids.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div><b>{total}</b> total</div>
                      {avail > 0 && <div className="text-amber-700">{avail} tersedia</div>}
                    </td>
                    <td className="px-4 py-3 min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <Progress value={r.progress_pct || 0} className="h-1.5 flex-1" />
                        <span className="text-[11px] font-semibold text-[#5C6066] w-9 text-right">{r.progress_pct || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold border ${PAY_TONE[r.payment_status] || statusBadge(r.payment_status)}`}>{r.payment_status}</span>
                      <div className="text-[10px] text-[#8C9096] mt-0.5">{formatIDR(r.paid)} / {formatIDR(r.total_value)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/bookings/${r.booking_id}?tab=execution`} className="text-xs font-semibold text-[#B7A175] hover:text-[#A08A5D]" data-testid={`bt-open-${r.booking_code}`}>Lihat Tugas →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
